import { useCallback, useEffect, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { useNotifications } from '../../../lib/NotificationContext.tsx';
import { useGLAccounts } from '../../../hooks/useGLAccounts.ts';
import { User } from '../../../api/accessControl/User.ts';
import { type RecategorizeCategoryType } from '../../../types/enums.ts';
import { saveFailureToast, type DocumentSavePayload } from '../utils/documentSave.ts';
import { billConversionRefusalMessage, documentConflictMessage } from '../../../lib/apiErrors.ts';
import type { MatchBillPart, TFICDocument } from '../types/Document.ts';
import type { BillTodo } from '../types/billTodo.ts';
import type { MatchTransactionPart } from '../../../pages/app/partner/pages/partnerUnmatchedTransactionsPage/types.ts';
import { useDocumentDrawer, useOpenDocument } from '../DocumentDrawerProvider.tsx';
import { DocumentDrawerDisplayMode, DrawerAutoAdvanceTrigger } from '../types/drawer.ts';
import type { BillTodoType } from '../../../generated/enums.ts';
import type { BillDuplicate } from '../../../components/specific/PossibleDuplicates/possibleDuplicates.ts';
import {
    convertBillToEuro,
    deleteDocument,
    dismissBillTodo,
    fetchBillTodos,
    markDocumentAsCreditCardStatement,
    recategorizeDocument,
    saveDocument,
    unmatchAndDeleteBill,
    unmatchDocument,
    useBillTodos,
    useDocument,
    useDocumentContent,
    useDocumentLoadFailed,
    useDrawerPossibleDuplicates,
    useMatchDetails,
} from './documentDrawerApi.ts';

export type SaveOptions = { handOff: boolean };

const NO_TODOS: BillTodo[] = [];

function isNothingLeftToCheck(reason: TFICDocument['reason'] | undefined, todos: BillTodo[] | null): boolean {
    return reason === null && todos !== null && todos.length === 0;
}

export function useDocumentDrawerService(currentUser: User) {
    const { dispatch } = useNotifications();
    const { close, documentHandled, notifyDocumentChanged, open: openDocument } = useDocumentDrawer();
    const session = useOpenDocument();
    const documentId = session?.documentId ?? null;
    const businessId = session?.businessId ?? null;
    const isOpen = session !== null;

    const { glAccounts, isLoading: glAccountsLoading } = useGLAccounts(currentUser);
    const documentQuery = useDocument(businessId, documentId);
    const documentLoadFailed = useDocumentLoadFailed(businessId, documentId);
    const contentQuery = useDocumentContent(businessId, documentId);
    const matchQuery = useMatchDetails(documentQuery.data?.is_matched ? documentId : null);
    const billId = documentQuery.data?.bill_id ?? null;
    const todosQuery = useBillTodos(billId);

    const document = useMemo<TFICDocument | null>(
        () =>
            documentQuery.data
                ? { ...documentQuery.data, file_blob: contentQuery.data ?? undefined }
                : null,
        [documentQuery.data, contentQuery.data],
    );

    const isWaitingForMatch =
        documentQuery.data?.is_matched === true && !matchQuery.data && !matchQuery.isError;
    const isDocumentLoading = documentQuery.isLoading || isWaitingForMatch;

    const documentError =
        documentQuery.isError || documentLoadFailed
            ? 'Er is een onbekende fout opgetreden. Probeer het later opnieuw.'
            : null;

    const lineItemsNeedingAttention = useMemo(() => {
        const documentDrawerOptions = session?.documentDrawerOptions;
        if (!documentDrawerOptions) {
            return [];
        }
        if (documentDrawerOptions.lineItemsNeedingAttention?.length) {
            return documentDrawerOptions.lineItemsNeedingAttention;
        }
        const message = documentDrawerOptions.allLineItemsMessage;
        if (!message) {
            return [];
        }
        return (documentQuery.data?.editable_bill?.line_items ?? [])
            .map((item) => item.id)
            .filter((id): id is string => Boolean(id))
            .map((lineItemId) => ({ lineItemId, message }));
    }, [session, documentQuery.data]);

    const refetchDocument = documentQuery.refetch;
    const refetchTodos = todosQuery.refetch;
    const todosAfterSave = useCallback(
        async (savedBillId: string | null): Promise<BillTodo[] | null> => {
            try {
                if (savedBillId && savedBillId === billId) {
                    const { data, isError } = await refetchTodos();
                    return isError ? null : data ?? NO_TODOS;
                }
                return await fetchBillTodos(savedBillId);
            } catch (error) {
                Sentry.captureException(error);
                return null;
            }
        },
        [billId, refetchTodos],
    );

    const onSave = useCallback(
        async (payload: DocumentSavePayload, options: SaveOptions = { handOff: true }): Promise<boolean> => {
            try {
                await saveDocument(payload);
                const saved = await refetchDocument();
                const todos = await todosAfterSave(saved.data?.bill_id ?? null);
                notifyDocumentChanged(payload.document_id);
                dispatch({
                    title: 'Document opgeslagen',
                    description: 'Het document is succesvol bijgewerkt',
                    type: 'success',
                });
                if (documentId && isNothingLeftToCheck(saved.data?.reason, todos) && options.handOff) {
                    documentHandled(documentId, DrawerAutoAdvanceTrigger.Save);
                    return true;
                }
                return false;
            } catch (error) {
                const conflictMessage = documentConflictMessage(error);
                if (conflictMessage) {
                    alert(conflictMessage);
                } else {
                    dispatch(saveFailureToast(error));
                }
                throw error;
            }
        },
        [documentId, refetchDocument, todosAfterSave, dispatch, documentHandled, notifyDocumentChanged],
    );

    const onDismissTodo = useCallback(
        async (todoType: BillTodoType, options: SaveOptions): Promise<void> => {
            if (!billId || !documentId) {
                return;
            }
            try {
                await dismissBillTodo(billId, todoType);
                const { data: remainingTodos, isError } = await refetchTodos();
                notifyDocumentChanged(documentId);
                const todos = isError ? null : remainingTodos ?? null;
                if (isNothingLeftToCheck(documentQuery.data?.reason, todos) && options.handOff) {
                    documentHandled(documentId, DrawerAutoAdvanceTrigger.Save);
                }
            } catch (error) {
                Sentry.captureException(error);
                dispatch({
                    title: 'Fout opgetreden',
                    description: 'De taak kon niet genegeerd worden. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
        },
        [billId, documentId, documentQuery.data?.reason, refetchTodos, notifyDocumentChanged, documentHandled, dispatch],
    );

    const onDuplicateDeleted = useCallback(
        async (bill: BillDuplicate) => {
            if (documentId && bill.document_id === documentId) {
                documentHandled(documentId, DrawerAutoAdvanceTrigger.Delete);
                return;
            }
            notifyDocumentChanged(bill.document_id);
            if (billId) {
                await refetchTodos();
            }
        },
        [billId, documentId, documentHandled, notifyDocumentChanged, refetchTodos],
    );
    const possibleDuplicates = useDrawerPossibleDuplicates(openDocument, onDuplicateDeleted);
    const { open: openDuplicates, close: closeDuplicates, modalProps: duplicatesModalProps } = possibleDuplicates;
    useEffect(() => {
        closeDuplicates();
    }, [documentId, closeDuplicates]);
    const onShowDuplicates = useCallback(() => {
        if (billId) {
            openDuplicates(billId);
        }
    }, [billId, openDuplicates]);
    const viewDuplicate = duplicatesModalProps.onView;
    const possibleDuplicatesModal = useMemo(
        () => ({
            ...duplicatesModalProps,
            onView: (bill: BillDuplicate) => {
                if (bill.document_id === documentId) {
                    closeDuplicates();
                    return;
                }
                viewDuplicate(bill);
            },
        }),
        [duplicatesModalProps, viewDuplicate, documentId, closeDuplicates],
    );

    const onConvertToEuro = useCallback(async () => {
        if (!documentId) {
            return;
        }
        try {
            const result = await convertBillToEuro(documentId);
            await refetchDocument();
            notifyDocumentChanged(documentId);
            dispatch(
                result.was_converted
                    ? {
                          title: 'Omgerekend naar euro',
                          description: 'Het document is omgerekend naar euro',
                          type: 'success',
                      }
                    : {
                          title: 'Niet omgerekend',
                          description: 'Er is geen koers beschikbaar voor deze factuurdatum',
                          type: 'error',
                      },
            );
        } catch (error) {
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                await refetchDocument();
                alert(conflictMessage);
                return;
            }
            const refusal = billConversionRefusalMessage(error);
            if (refusal) {
                dispatch({ title: 'Niet omgerekend', description: refusal, type: 'error' });
                return;
            }
            Sentry.captureException(error);
            dispatch(saveFailureToast(error));
        }
    }, [documentId, refetchDocument, notifyDocumentChanged, dispatch]);

    const isMatched = documentQuery.data?.is_matched ?? false;
    const onDelete = useCallback(async () => {
        if (!documentId) {
            return;
        }
        const matchedBillId = isMatched ? billId : null;
        try {
            await (matchedBillId ? unmatchAndDeleteBill(matchedBillId) : deleteDocument(documentId));
            dispatch({
                title: 'Document verwijderd',
                description: matchedBillId
                    ? 'Het document is ontkoppeld en verwijderd'
                    : 'Het document is succesvol verwijderd',
                type: 'success',
            });
            notifyDocumentChanged(documentId);
            documentHandled(documentId, DrawerAutoAdvanceTrigger.Delete);
        } catch (error) {
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                alert(conflictMessage);
            } else {
                dispatch({
                    title: 'Fout bij verwijderen',
                    description:
                        'Er is een fout opgetreden bij het verwijderen van het document. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
            throw error;
        }
    }, [documentId, isMatched, billId, dispatch, documentHandled, notifyDocumentChanged]);

    const onMarkAsCreditCardStatement = useCallback(async () => {
        if (!documentId) {
            return;
        }
        try {
            await markDocumentAsCreditCardStatement(documentId);
            dispatch({
                title: 'Document hercategoriseerd',
                description: 'Het document is gemarkeerd als creditcard afschrift.',
                type: 'success',
            });
            notifyDocumentChanged(documentId);
            documentHandled(documentId, DrawerAutoAdvanceTrigger.MarkAsCreditCardStatement);
        } catch (error) {
            const conflictMessage = documentConflictMessage(error);
            if (conflictMessage) {
                await refetchDocument();
                alert(conflictMessage);
            } else {
                Sentry.captureException(error);
                dispatch({
                    title: 'Fout opgetreden',
                    description:
                        'Het document kon niet worden gemarkeerd als creditcard afschrift. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
        }
    }, [documentId, refetchDocument, dispatch, documentHandled, notifyDocumentChanged]);

    const onRecategorize = useCallback(
        async (category: RecategorizeCategoryType) => {
            if (!documentId) {
                return;
            }
            try {
                await recategorizeDocument(documentId, category);
                if (category === 'archive') {
                    dispatch({
                        title: 'Document gearchiveerd',
                        description: 'Het document staat nu in het archief.',
                        type: 'success',
                    });
                    documentHandled(documentId, DrawerAutoAdvanceTrigger.Archive);
                    return;
                }
                dispatch({
                    title: 'Document hercategoriseerd',
                    description:
                        category === 'credit_card_statement'
                            ? 'Het document is gemarkeerd als creditcard afschrift.'
                            : 'Het document wordt opnieuw verwerkt.',
                    type: 'success',
                });
                documentHandled(documentId, DrawerAutoAdvanceTrigger.Recategorize);
            } catch (error) {
                Sentry.captureException(error);
                dispatch({
                    title: 'Fout opgetreden',
                    description: 'Er is een fout opgetreden. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
        },
        [documentId, dispatch, documentHandled],
    );

    const refetchMatch = matchQuery.refetch;
    const onUnmatch = useCallback(
        async (matchId: string, bills: MatchBillPart[], transactions: MatchTransactionPart[]) => {
            try {
                await unmatchDocument(
                    matchId,
                    bills.map((bill) => bill.id),
                    transactions.map((transaction) => transaction.id),
                );
                const { data: refreshedDocument } = await refetchDocument();
                if (refreshedDocument?.is_matched) {
                    await refetchMatch();
                }
            } catch (error) {
                dispatch({
                    title: 'Fout opgetreden',
                    description: 'Er is een fout opgetreden bij het ontkoppelen. Probeer het later opnieuw.',
                    type: 'error',
                });
            }
        },
        [refetchDocument, refetchMatch, dispatch],
    );

    return {
        isOpen,
        documentId,
        businessId,
        document,
        isDocumentLoading,
        isDocumentContentLoading: contentQuery.isLoading,
        documentError,
        matchData: matchQuery.data ?? null,
        billTodos: todosQuery.data ?? NO_TODOS,
        possibleDuplicatesModal,
        lineItemsNeedingAttention,
        glAccounts,
        glAccountsLoading,
        initialTab: session?.initialTab,
        displayMode: session?.displayMode ?? DocumentDrawerDisplayMode.FollowReason,
        close,
        onSave,
        onConvertToEuro,
        onDelete,
        onRecategorize,
        onMarkAsCreditCardStatement,
        onUnmatch,
        onDismissTodo,
        onShowDuplicates,
    };
}
